import React from 'react';
import './index.less';
import { useStore } from '../../../../store';
import { useParams } from 'react-router-dom';
import quizIllustration from '../../../../../../assets/student-training/component/quiz-bg.png';

interface IQuizOption {
  indicator?: string;
  title: string;
  hint: string;
  isCorrect: boolean;
}

interface IQuizProps {
  title: string;
  description?: string;
  question?: string;
  options: IQuizOption[];
  onCompleted?: () => void;
  quizImg?: string;
}

const INDICATORS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const HelpCircleIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const XIcon: React.FC<{ color?: string }> = ({ color = '#a3a3a3' }) => (
  <svg
    width="88"
    height="88"
    viewBox="0 0 88 88"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="44" cy="44" r="41" stroke={color} strokeWidth="4" />
    <path
      d="M30 30L58 58M58 30L30 58"
      stroke={color}
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

const PurpleCheckIcon: React.FC<{ color?: string }> = ({
  color = '#262626',
}) => (
  <svg
    width="88"
    height="88"
    viewBox="0 0 88 88"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="44" cy="44" r="41" stroke={color} strokeWidth="4" />
    <path
      d="M27 46L39 58L61 32"
      stroke={color}
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const Quiz: React.FunctionComponent<IQuizProps> = (props) => {
  const {
    title = '情景問題一',
    description,
    question,
    options,
    onCompleted,
    quizImg,
  } = props;

  const { courses } = useStore();
  const param = useParams();
  const p_course = param.course;
  const course = courses[p_course || ''];
  const settings = course?.components;

  options?.forEach((item, index) => {
    item.indicator = INDICATORS[index];
  });

  const [state, setState] = React.useState<{
    submitted: boolean;
    selected: IQuizOption | undefined;
    selectedValue: string;
  }>({
    submitted: false,
    selected: undefined,
    selectedValue: '',
  });

  const onCompletedRef = React.useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  React.useEffect(() => {
    if (state.submitted && state.selected?.isCorrect) {
      onCompletedRef.current?.();
    }
  }, [state.selected?.isCorrect, state.submitted]);

  if (!p_course) {
    return <div>Loading</div>;
  }

  const onSelect = (indicator?: string) => {
    if (state.submitted || !indicator) {
      return;
    }
    setState((prev) => ({
      ...prev,
      selectedValue: indicator,
      selected: options.find((option) => option.indicator === indicator),
    }));
  };

  const submit = () => {
    if (!state.selected) {
      return;
    }
    setState((prev) => ({ ...prev, submitted: true }));
  };

  const retry = () => {
    setState({ submitted: false, selected: undefined, selectedValue: '' });
  };

  const showResult = state.submitted && Boolean(state.selected);
  const isCorrect = Boolean(state.selected?.isCorrect);
  const submitLabel = settings?.submitLabel || '提交';
  const retryLabel = isCorrect
    ? settings?.backLabel || '返回'
    : settings?.tryLabel || '再试试';
  const correctLabel = settings?.correctLabel || '正確答案';
  const wrongLabel = settings?.wrongLabel || '錯誤答案';

  return (
    <div
      className={`civil-quiz${
        showResult
          ? isCorrect
            ? ' is-correct-result'
            : ' is-wrong-result'
          : ''
      }`}
    >
      <div className="civil-quiz-figure">
        <img src={quizImg || quizIllustration} alt="" />
        <div className="civil-quiz-badge">
          <span className="civil-quiz-badge-icon">
            <HelpCircleIcon />
          </span>
          <span className="civil-quiz-badge-text">{title}</span>
        </div>
      </div>

      <div className="civil-quiz-main">
        {!showResult && (
          <div className="civil-quiz-question-view">
            <div className="civil-quiz-prompt">
              {description && (
                <p
                  className="civil-quiz-desc"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              {question && (
                <p
                  className="civil-quiz-ask"
                  dangerouslySetInnerHTML={{ __html: question }}
                />
              )}
            </div>

            <div className="civil-quiz-options" role="radiogroup">
              {options?.map((option, index) => {
                const checked = option.indicator === state.selectedValue;
                return (
                  <div
                    key={index}
                    role="radio"
                    aria-checked={checked}
                    tabIndex={0}
                    className={`civil-quiz-option${
                      checked ? ' is-selected' : ''
                    }`}
                    onClick={() => onSelect(option.indicator)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(option.indicator);
                      }
                    }}
                  >
                    <span className="civil-quiz-option-dot" />
                    <span className="civil-quiz-option-text">
                      {option.title}
                    </span>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="civil-quiz-btn"
              disabled={!state.selected}
              onClick={submit}
            >
              <span>{submitLabel}</span>
              <ArrowRightIcon />
            </button>
          </div>
        )}

        {showResult && (
          <div
            className={`civil-quiz-result-view${
              isCorrect ? ' is-correct' : ' is-wrong'
            }`}
          >
            <div className="civil-quiz-result-icon">
              {isCorrect ? (
                <PurpleCheckIcon color="#262626" />
              ) : (
                <XIcon color="#a3a3a3" />
              )}
            </div>
            <div className="civil-quiz-result-title">
              {isCorrect ? correctLabel : wrongLabel}{' '}
              {/*<span className="civil-quiz-result-indicator">*/}
              {/*  {state.selected?.indicator}*/}
              {/*</span>*/}
            </div>
            <div className="civil-quiz-result-divider" />
            <p
              className="civil-quiz-result-hint"
              dangerouslySetInnerHTML={{ __html: state.selected?.hint || '' }}
            />
            <button type="button" className="civil-quiz-btn" onClick={retry}>
              <span>{retryLabel}</span>
              <ArrowRightIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

Quiz.defaultProps = {
  onCompleted: undefined,
  description: undefined,
  question: undefined,
  quizImg: undefined,
};

export default Quiz;
